import './entity.less';
import './offers.less';
import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import cn from 'classname';

import SelectField from '../../../../shared/form-elements/select/field';
import Currency from './currency';
import Targets from './targets';

import min from 'lodash/min';
import max from 'lodash/max';
import get from 'lodash/get';
import noop from 'lodash/noop';

const Offers = props => {
	if (props.fields.length === 0) return null;
	return (
		<tr className="offers-form__wrap">
			<td colSpan={5}>
				<table className="offers-form__table">
					<thead className="entity-wrap__table-head">
						<tr>
							<th className="offer-title">Offers</th>
							<th className="geo">Geo</th>
							<th className="payout">Payout</th>
							<th className="currencies" />
							<td className="actions" />
						</tr>
					</thead>
					<tbody className="entity-wrap__table-body">
						{props.fields
							.map((parentName, index, fields) => {
								const res = [];

								const targetsAmount = props.fields.get(index)['targets'] && props.fields.get(index)['targets'].length;
								const shouldShowTargets =
									props.listOfActiveOffersPanIndex[props.parentIndex] &&
									props.listOfActiveOffersPanIndex[props.parentIndex].indexOf(index) > -1 &&
									!!targetsAmount;

								const firstRowHeight = 83;
								const rowHeight = 56;
								const subtractOnHidePixelHeight = !targetsAmount
									? 0
									: index !== 0 ? -((targetsAmount - 1) * rowHeight + firstRowHeight) : 0;
								const addOnShowPixelHeight = !targetsAmount
									? 0
									: index !== 0 ? (targetsAmount - 1) * rowHeight + firstRowHeight : 0;

								const onToggleTargetsPan = props.fields.get(index)
									? shouldShowTargets
										? handleToggleTargets(
											props.parentIndex,
											props.hideOfferTargets,
											props.updateShownTargetsPixelHeights,
											subtractOnHidePixelHeight
										)
										: handleToggleTargets(
											props.parentIndex,
											props.showOfferTargets,
											props.updateShownTargetsPixelHeights,
											addOnShowPixelHeight
										)
									: noop;

								// offer row
								res.push(
									<Offer
										key={index}
										type="offer"
										parentName={parentName}
										parentIndex={props.parentIndex}
										payoutCurrencies={props.payoutCurrencies}
										index={index}
										intl={props.intl}
										placeholder={props.intl('Select offer')}
										fields={props.fields}
										entityLookup={props.affiliatesOffersLookups}
										selectedCurrenciesPanIndexMap={props.selectedCurrenciesPanIndexMap}
										onChangeCurrency={props.onChangeCurrency}
										onToggleTargetsPan={onToggleTargetsPan}
										className={cn({
											'is-show-targets': shouldShowTargets,
											'modify-cursor-pointer':
												props.fields.get(index)['targets'] && props.fields.get(index)['targets'].length,
											'last-offer': index === 0
										})}
										shouldShowTargets={shouldShowTargets}
										targetsAmount={targetsAmount}
									/>,
									<tr key={`offers-padding-${index}`} className="padding-tr" />
								);

								// targeting row
								shouldShowTargets &&
									res.push(
										<FieldArray
											name={`${parentName}.targets`}
											intl={props.intl}
											parentIndex={index}
											component={Targets}
											nullifiedBoosts={props.nullifiedBoosts}
											payoutCurrencies={props.payoutCurrencies}
											nullifyBoostOnSubmit={props.nullifyBoostOnSubmit}
										/>
									);

								return res;
							})
							.reverse()}
					</tbody>
				</table>
			</td>
		</tr>
	);
};

Offers.propTypes = {
	intl: PropTypes.func,
	fields: PropTypes.object,
	affiliatesOffersLookups: PropTypes.func,
	parentIndex: PropTypes.number,
	selectedCurrenciesPanIndexMap: PropTypes.object,
	onChangeCurrency: PropTypes.func,
	nullifyBoostOnSubmit: PropTypes.func,
	nullifiedBoosts: PropTypes.array
};

const Offer = ({
	className,
	parentIndex,
	type,
	parentName,
	payoutCurrencies,
	index,
	intl,
	fields,
	placeholder,
	entityLookup,
	selectedCurrenciesPanIndexMap,
	onChangeCurrency,
	onToggleTargetsPan,
	shouldShowTargets,
	targetsAmount
}) => {
	const rowOffer = fields.get(index).offer;
	const image = rowOffer && rowOffer[0].image;

	const firstRowHeight = 60;
	const rowHeight = 55;

	return (
		<tr className={`entity-offer ${className}`} onClick={e => onToggleTargetsPan(index)}>
			<td className="entity-type">
				{shouldShowTargets && targetsAmount ? (
					<div
						className="vertical-line"
						style={{ height: targetsAmount ? firstRowHeight + (targetsAmount - 1) * rowHeight : firstRowHeight }}
					/>
				) : null}
				<Field
					name={`${parentName}.${type}`}
					placeholder={placeholder}
					image={image}
					component={SelectFieldWithImage}
					multi={false}
					disabled={!!fields.get(index)}
					showLabel={false}
					options={text => entityLookup(parentIndex, text)}
				/>
			</td>
			<td className="entity-geo">
				<span>{calculateTotalGeo(index, fields)}</span>
			</td>
			<td className="entity-payout">
				<span>
					{calculateMinMaxPayout(index, fields, [
						(selectedCurrenciesPanIndexMap[parentIndex] && selectedCurrenciesPanIndexMap[parentIndex][index]) || 'usd'
					])}
				</span>{' '}
			</td>
			<td className="entity-currencies">
				<Currency
					availableCurrencies={payoutCurrencies}
					currentFieldTargets={get(fields.getAll()[index], 'targets')}
					selectedCurrenciesPanIndexMap={selectedCurrenciesPanIndexMap}
					fieldIndex={index}
					parentIndex={parentIndex}
					onChangeCurrency={onChangeCurrency}
				/>
			</td>
			<td className="entity-actions">
				<div className="actions__wrap">
					<span className="toggle" />
				</div>
			</td>
		</tr>
	);
};

const calculateTotalGeo = (index, fields) => {
	const targets = fields.getAll()[index]['targets'] || [];
	return targets.filter(entity => entity.rub || entity.usd).length;
};

const calculateMinMaxPayout = (index, fields, currency = 'rub') => {
	const targets = fields.getAll()[index]['targets'] || [];
	const getAllAmount = targets.map(entity => +entity[currency]);
	const minValue = min(getAllAmount) || 0;
	const maxValue = max(getAllAmount) || 0;
	return `${minValue} - ${maxValue}`;
};

const SelectFieldWithImage = props => {
	return (
		<div>
			<SelectField {...props}>
				<img src={props.image || '//placehold.it/30x30'} alt="offer img" className="select-image select-image__offer" />
			</SelectField>
		</div>
	);
};

const handleToggleTargets = (parentIndex, handler, updateShownTargetsPixelHeights, targetsAmount) => index => {
	updateShownTargetsPixelHeights(parentIndex, targetsAmount);
	handler(index, parentIndex);
};

SelectFieldWithImage.propTypes = {
	image: PropTypes.string
};

Offer.propTypes = {
	className: PropTypes.string,
	type: PropTypes.string,
	parentName: PropTypes.string,
	index: PropTypes.any,
	parentIndex: PropTypes.number,
	intl: PropTypes.func,
	fields: PropTypes.object,
	placeholder: PropTypes.string,
	entityLookup: PropTypes.func,
	selectedCurrenciesPanIndexMap: PropTypes.object,
	onChangeCurrency: PropTypes.func,
	onToggleTargetsPan: PropTypes.func,
	shouldShowTargets: PropTypes.bool,
	targetsAmount: PropTypes.number
};

export default Offers;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/profile/affiliates/offers.js